import { request } from './client'

/* W-122 — five roles, every capability, one grid.

   ROLES ARE OURS, NOT THE PROPERTY SYSTEM'S. Nothing about permissions crosses that
   interface: they tell us who a person is, and this grid decides what they may do.
   That separation is what lets an administrator fix an access problem at eight in the
   morning without anyone else's help.

   THE GRANT HAS THREE VALUES, NOT TWO, and has had since Sprint 0. This is the first
   screen that lets anybody see it. */

export type Grant = 'DENIED' | 'ALLOWED' | 'ALLOWED_WITH_REASON'

/* PUBLIC_AREAS, NOT AREAS. The C# member is `Area.Areas` and its wire name is
   `PUBLIC_AREAS` — the vocabulary spells every word by hand precisely so that the name
   on the wire can differ from the name in the code, and this is one that does. Writing
   this list from the enum rather than from `Wire.Of` put 'AREAS' here, and the grid drew
   a raw `PUBLIC_AREAS` in its Area column until somebody looked at it. */
/* **`LOST_PROPERTY` WAS HERE AND IS GONE** (Sprint 60). The client settled on
   18 September that lost property is handed to the front desk and is not in this product's
   scope, so the one permission in that area — granted, reason-bearing and enforced by
   nothing for sixty sprints — was removed rather than left saying "nothing yet" for ever. */
export type PermissionArea =
  | 'ROOMS' | 'WORK' | 'REQUESTS' | 'FAULTS' | 'PUBLIC_AREAS'
  | 'INVENTORY' | 'REPORTS' | 'FILES' | 'ADMINISTRATION'

export interface GrantCell {
  grant: Grant
  /** What the seed says it should be. Sent so the screen can mark what has been
   *  changed without keeping its own copy of the defaults — which would be a second
   *  source for a fact that lives in the seed. */
  default: Grant
  isChanged: boolean
}

export interface CapabilityRow {
  code: string
  area: PermissionArea
  /** May this one sensibly be "allowed, with a reason recorded"? The screen offers the
   *  third state ONLY where it means something: a reason nobody can type is a setting
   *  that silently means Allowed. */
  mayNeedAReason: boolean
  /** Where the product actually checks it, in words — including "nothing yet" for the
   *  handful that are granted and enforced by no screen. NOT TRANSLATED: it names
   *  transitions and screen codes, and it is written for the person configuring the
   *  system rather than for the floor. */
  enforcedBy: string
  /** Keyed by role id, and EVERY role has a cell. An absent row means Denied, and a
   *  grid with a hole in it would read as "unknown". */
  grants: Record<string, GrantCell>
}

export interface GridRole {
  roleId: string
  code: string
  name: string
  nameAr: string | null
  isSystem: boolean
}

export interface PermissionGrid {
  roles: GridRole[]
  capabilities: CapabilityRow[]
  changedFromDefault: number
  mayEdit: boolean
}

export interface GrantChange {
  roleId: string
  permissionCode: string
  grant: Grant
}

export function fetchPermissionGrid(
  options: { signal?: AbortSignal } = {},
): Promise<PermissionGrid> {
  return request<PermissionGrid>(
    '/roles/permissions', options.signal ? { signal: options.signal } : {})
}

/** ALL OR NOTHING. A half-applied permission grid is a state nobody meant, and the
 *  person who pressed Save has no way to tell which half landed. */
export function savePermissionGrid(changes: GrantChange[]): Promise<PermissionGrid> {
  return request<PermissionGrid>('/roles/permissions', { method: 'PUT', body: { changes } })
}
